// deps
import { useMemo, useContext, useCallback } from "react";

// internal-deps
import {
    PayLaterLinkedData,
    PayLaterPaymentGroupActions,
    PayLaterPaymentMethodData,
    PayLaterSpecificActions,
    isAmazonPayLater,
} from "@swiggy-private/payment-ui";
import { ApiFetchState, PAYLATER_TYPES } from "@swiggy-private/payment-core";
import {
    User,
    UserContext,
    useNavigate,
    CheckoutRoute,
    getUserFromUserContextValue,
    ConfigContext,
} from "@swiggy-private/checkout-core";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// interfaces
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// contexts
import { PaylaterLinkedContext } from "@payment-client/contexts/PaylaterLinkedContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// hooks
import { usePaymentActions } from "./UsePaymentActions";

// helpers
import {
    linkAccountImpressionEvent,
    onKeyFactStatementClickEvent,
    onLinkPayLaterClickEvent,
    payLaterMethodsImpressionEvent,
} from "@payment-client/analytics/events/Paylater";

// helpers
import { createRedirectToSimplHandler } from "@payment-client/helpers/SimplPaylaterHelper";
import { recordKeyFactsClick } from "@payment-client/helpers/AmazonPayNREvents";
import { TEXTS } from "@payment-client/constants/PayLater";
import { useAmazonPayHandler } from "./UseAmazonPayHandler";
import { WebNavigateRequestPayload } from "@swiggy-private/native-bridge";
import { getBridgeSdk } from "@swiggy-private/checkout-core";

interface UsePayLaterHandlerReturnType {
    paymentActions: PayLaterPaymentGroupActions;
    payLaterSpecificActions: PayLaterSpecificActions;
    user?: User;
}

interface UsePayLaterHandlerProps {
    onPay: PaymentGroupHandlerProps["onPay"];
    onSelect: PaymentGroupHandlerProps["onSelect"];
    paymentAmount: string;
}

export const usePayLaterHandler = (props: UsePayLaterHandlerProps): UsePayLaterHandlerReturnType => {
    const { onSelect, onPay } = props;
    const { user: userContextValue } = useContext(UserContext);
    const { paylaterState } = useContext(PaylaterLinkedContext);
    const { linkAmazonPaySdk, getAmazonPaySdkStatus } = useAmazonPayHandler(
        Number(props.paymentAmount),
    );
    const { state: amazonPaySdkState, sdkFetchInProgress } = getAmazonPaySdkStatus();

    const actions = usePaymentActions({ onSelect, onPay });
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const navigateTo = useNavigate();
    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);
    const { paymentResponse = null } = useContext(ShowPaymentContext);
    const { simplPostLinkingRedirectUrl = "" } = useContext(ConfigContext);

    const checkPayLaterLinkStatus = useCallback(
        (methodData: PayLaterPaymentMethodData, _refetch?: boolean): [ApiFetchState, PayLaterLinkedData] => {
            if (isAmazonPayLater(methodData.paymentCode)) {
                const isLinked = amazonPaySdkState?.apl?.linked || false;
                const isSdkAvailable = !!amazonPaySdkState?.available;
                const hasSufficientCredit = amazonPaySdkState?.apl?.isCreditLimitSufficient !== false;

                // Allow unlinked users to proceed to linking flow;
                // only gate on credit limit once the account is linked.
                const isEnabled = !!methodData?.enabled && isSdkAvailable && (!isLinked || hasSufficientCredit);

                return [
                    sdkFetchInProgress ? ApiFetchState.LOADING : ApiFetchState.SUCCESS,
                    {
                        linked: isLinked,
                        enabled: isEnabled,
                        disableMessage: amazonPaySdkState?.apl?.disableMessage || methodData?.meta?.disableMessage,
                        creditLimit: amazonPaySdkState?.apl?.creditLimit,
                        keyFactStatementUrl: amazonPaySdkState?.apl?.keyFactStatementUrl,
                        isCreditLimitSufficient: amazonPaySdkState?.apl?.isCreditLimitSufficient,
                    },
                ];
            }
            const { linked, meta, fetchState, enabled, disableMessage } = paylaterState[methodData.name];
            return [fetchState, { linked, meta, enabled, disableMessage }];
        },
        [amazonPaySdkState, paylaterState, sdkFetchInProgress],
    );

    const linkAccountActions: PayLaterPaymentGroupActions["linkAccountActions"] = useMemo(
        () => ({
            onProceed: (payLaterCode: string, mobileNumber: string): void => {
                navigateTo(CheckoutRoute.LINK_PAYLATER, { payLaterCode, mobileNumber });
            },
        }),
        [navigateTo],
    );

    const linkPayLater: PayLaterPaymentGroupActions["linkPayLater"] = useMemo(
        () => ({
            /*
              Handles the proceed action for pay later scenarios.In this pay later code indicating the type of pay later service and
              the redirection URL for SIMPL pay later service. Required if payLaterCode is PAYLATER_TYPES.SIMPL.
            */
            onProceed: (payLaterCode: string, redirectionUrl?: string): void => {
                if (payLaterCode === PAYLATER_TYPES.SIMPL) {
                    createRedirectToSimplHandler(redirectionUrl, paymentResponse, simplPostLinkingRedirectUrl);
                } else if (payLaterCode === PAYLATER_TYPES.AMAZON_PAYLATER) {
                    linkAmazonPaySdk(payLaterCode);
                } else {
                    navigateTo(CheckoutRoute.LINK_PAYLATER_MOBILE, { payLaterCode });
                }
            },
        }),
        [navigateTo, paymentResponse, simplPostLinkingRedirectUrl, linkAmazonPaySdk],
    );

    const onKeyFactStatementClick = useCallback(
        (url: string) => {
            recordKeyFactsClick();
            onKeyFactStatementClickEvent(PAYLATER_TYPES.AMAZON_PAYLATER, clickEvent);
            const data: WebNavigateRequestPayload = {
                callbackUrl: window.location.href,
                openAsExternal: true,
                showBack: false,
                title: TEXTS.KEY_FACT_STATEMENT_TITLE,
                url,
                showLoader: true,
            };
            getBridgeSdk()
                .webNavigate(data)
                .catch(() => void 0);
        },
        [clickEvent],
    );

    const analyticsPayLater: PayLaterPaymentGroupActions["analyticsPayLater"] = useMemo(
        () => ({
            onClickLink: (payLaterCode: string): void => {
                onLinkPayLaterClickEvent(payLaterCode, clickEvent);
            },
            paylaterMethodImpression: (payLaterCode: string, message?: string): void => {
                payLaterMethodsImpressionEvent(payLaterCode, message, impressionEvent);
            },
            linkAccountImpression: (payLaterCode: string): void => {
                linkAccountImpressionEvent(payLaterCode, impressionEvent);
            },
        }),
        [clickEvent, impressionEvent],
    );

    const payLaterSpecificActions: PayLaterSpecificActions = useMemo(
        () => ({
            checkPayLaterLinkStatus,
            linkAccountActions,
            linkPayLater,
            analyticsPayLater,
            onKeyFactStatementClick,
        }),
        [checkPayLaterLinkStatus, linkAccountActions, linkPayLater, analyticsPayLater, onKeyFactStatementClick],
    );

    const paymentActions: PayLaterPaymentGroupActions = useMemo(
        () => ({
            ...actions,
            ...payLaterSpecificActions,
        }),
        [actions, payLaterSpecificActions],
    );

    return { paymentActions, payLaterSpecificActions, user };
};
